import type {
  DashboardWholesaleCustomerSnapshot,
  DashboardWholesaleSale,
} from '@pos/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { ProductSelect } from '../../components/ProductSelect';
import { Select } from '../../components/Select';
import {
  getSettings,
  listBranches,
  listBranchPricing,
  listProducts,
} from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import { fmtAmount } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';
import type { Product, ProductUnit, SystemSettings } from '../../types/domain';
import { WholesaleSalePrintModal } from './WholesaleSalePrintModal';

interface DraftLine {
  productId: string;
  unitId: string;
  quantity: string;
  unitPriceBaht: string;
}

const blankLine = (): DraftLine => ({
  productId: '',
  unitId: '',
  quantity: '1',
  unitPriceBaht: '',
});

const blankCustomer = (): Required<DashboardWholesaleCustomerSnapshot> => ({
  name: '',
  taxId: '',
  phone: '',
  address: '',
});

const unitId = (product: Product, unit: ProductUnit, index: number): string =>
  unit.id ?? `${product.id}:unit:${index + 1}`;

const bahtInput = (satang: number): string => (satang / 100).toFixed(2);

const parseSatang = (value: string): number | null => {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const [whole, decimal = ''] = normalized.split('.');
  const satang =
    Number(whole) * 100 + Number(decimal.padEnd(2, '0').slice(0, 2));
  return Number.isSafeInteger(satang) ? satang : null;
};

const parseQuantity = (value: string): number | null => {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,3})?$/.test(normalized)) return null;
  const quantity = Number(normalized);
  return quantity > 0 && quantity <= 1_000_000 ? quantity : null;
};

const defaultSettings: SystemSettings = {
  shopName: '',
  taxId: '',
  vatRate: 0,
  vatInclusive: true,
  receiptHeader: '',
  receiptFooter: '',
};

export function WholesaleSaleView() {
  const user = useSession((state) => state.user);
  const selectedBranchId = useUi((state) => state.branchId);
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const branchPricingQuery = useQuery({
    queryKey: ['branch-pricing'],
    queryFn: listBranchPricing,
  });
  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  });
  const branches = branchesQuery.data ?? [];
  const products = productsQuery.data ?? [];
  const branchPricing = branchPricingQuery.data ?? [];
  const settings = settingsQuery.data ?? defaultSettings;

  const allowedBranchIds = user
    ? visibleBranchIds(
        user,
        branches.map((branch) => branch.id),
      )
    : [];
  const allowedBranches = branches.filter((branch) =>
    allowedBranchIds.includes(branch.id),
  );
  const branchId =
    selectedBranchId && allowedBranchIds.includes(selectedBranchId)
      ? selectedBranchId
      : '';
  const [searchError, setSearchError] = useState('');
  const [searchKey, setSearchKey] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (searchKey > 0) searchRef.current?.querySelector('input')?.focus();
  }, [searchKey]);
  const [customer, setCustomer] = useState(blankCustomer);
  const [showCustomerFields, setShowCustomerFields] = useState(false);
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);
  const [formError, setFormError] = useState('');
  const [createdSale, setCreatedSale] = useState<DashboardWholesaleSale | null>(
    null,
  );

  const productById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const activeProducts = useMemo(
    () => products.filter((product) => product.active),
    [products],
  );
  const branchPriceByProduct = useMemo(
    () =>
      new Map(
        branchPricing.map((row) => [row.productId, row.prices[branchId]]),
      ),
    [branchId, branchPricing],
  );

  const normalizedLines = lines.map((line) => {
    const product = productById.get(line.productId);
    const unit = product?.units.find(
      (candidate, index) => unitId(product, candidate, index) === line.unitId,
    );
    const quantity = parseQuantity(line.quantity);
    const price = parseSatang(line.unitPriceBaht);
    return {
      draft: line,
      product,
      unit,
      quantity,
      price,
      amount:
        quantity !== null && price !== null ? Math.round(quantity * price) : 0,
    };
  });
  const totalAmount = normalizedLines.reduce(
    (total, line) => total + line.amount,
    0,
  );
  const selectedLineCount = normalizedLines.filter(
    (line) => line.product && line.unit,
  ).length;
  const selectedBranchName =
    allowedBranches.find((branch) => branch.id === branchId)?.name ?? '';
  const hasCustomerDetails = Object.values(customer).some(
    (value) => value.trim().length > 0,
  );
  const customerSummary = hasCustomerDetails
    ? customer.name.trim() || 'ระบุข้อมูลแล้ว'
    : 'ลูกค้าทั่วไป';
  const isPreparing =
    branchesQuery.isPending ||
    productsQuery.isPending ||
    branchPricingQuery.isPending;
  const hasLoadError =
    branchesQuery.isError ||
    productsQuery.isError ||
    branchPricingQuery.isError;

  const createSale = useMutation({
    mutationFn: (
      input: Parameters<typeof dashboardApi.createWholesaleSale>[0],
    ) => dashboardApi.createWholesaleSale(input),
    onSuccess: (sale) => {
      setCreatedSale(sale);
      setLines([blankLine()]);
      setCustomer(blankCustomer());
      setShowCustomerFields(false);
      setNote('');
      setFormError('');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : '';
      setFormError(
        message.includes('Insufficient stock')
          ? 'สต๊อกไม่เพียงพอ กรุณาตรวจสอบจำนวนแล้วลองอีกครั้ง'
          : 'บันทึกยอดขายไม่สำเร็จ กรุณาตรวจสอบข้อมูลและการเชื่อมต่อ',
      );
    },
  });

  const setLine = (index: number, patch: Partial<DraftLine>) => {
    setLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index ? { ...line, ...patch } : line,
      ),
    );
    setFormError('');
  };

  const selectProduct = (
    index: number,
    productId: string,
    barcode?: string,
  ) => {
    const product = productById.get(productId);
    const unitIndex = barcode
      ? Math.max(
          0,
          product?.units.findIndex((unit) => unit.barcode === barcode) ?? -1,
        )
      : 0;
    const selectedUnit = product?.units[unitIndex];
    if (!product || !selectedUnit) {
      setLine(index, {
        productId,
        unitId: '',
        unitPriceBaht: '',
      });
      return;
    }
    const selectedUnitId = unitId(product, selectedUnit, unitIndex);
    const price =
      selectedUnitId === product.baseUnitId
        ? (branchPriceByProduct.get(product.id) ?? selectedUnit.price)
        : selectedUnit.price;
    setLine(index, {
      productId,
      unitId: selectedUnitId,
      unitPriceBaht: bahtInput(price),
    });
  };

  const addProduct = (id: string, barcode?: string) => {
    const product = productById.get(id);
    if (!product || !product.units.length) return;
    const unitIndex = barcode
      ? Math.max(
          0,
          product.units.findIndex((unit) => unit.barcode === barcode),
        )
      : 0;
    const unit = product.units[unitIndex];
    const idOfUnit = unitId(product, unit, unitIndex);
    const draft = {
      productId: id,
      unitId: idOfUnit,
      quantity: '1',
      unitPriceBaht: bahtInput(
        idOfUnit === product.baseUnitId
          ? (branchPriceByProduct.get(id) ?? unit.price)
          : unit.price,
      ),
    };
    setLines((current) => {
      const empty = current.findIndex((line) => !line.productId);
      return empty >= 0
        ? current.map((line, index) => (index === empty ? draft : line))
        : current.length < 100
          ? [...current, draft]
          : current;
    });
    setSearchError('');
    setSearchKey((key) => key + 1);
  };

  const selectUnit = (index: number, nextUnitId: string) => {
    const product = productById.get(lines[index]?.productId ?? '');
    const selected = product?.units.find(
      (unit, unitIndex) => unitId(product, unit, unitIndex) === nextUnitId,
    );
    const price =
      product && nextUnitId === product.baseUnitId
        ? (branchPriceByProduct.get(product.id) ?? selected?.price ?? 0)
        : (selected?.price ?? 0);
    setLine(index, {
      unitId: nextUnitId,
      unitPriceBaht: bahtInput(price),
    });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!branchId) {
      setFormError('กรุณาเลือกสาขาที่ขาย');
      return;
    }
    if (
      normalizedLines.some(
        (line) =>
          !line.product ||
          !line.unit ||
          line.quantity === null ||
          line.price === null,
      )
    ) {
      setFormError('กรุณากรอกสินค้า หน่วย จำนวน และราคาให้ครบถ้วน');
      return;
    }

    const optionalCustomer = Object.fromEntries(
      Object.entries(customer)
        .map(([key, value]) => [key, value.trim()])
        .filter(([, value]) => value),
    ) as DashboardWholesaleCustomerSnapshot;

    createSale.mutate({
      requestId: crypto.randomUUID(),
      branchId,
      ...(Object.keys(optionalCustomer).length > 0
        ? { customer: optionalCustomer }
        : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
      lines: normalizedLines.map((line) => ({
        productId: line.product!.id,
        unitId: line.draft.unitId,
        quantity: line.quantity!,
        unitPrice: line.price!,
      })),
    });
  };

  const createdBranchName =
    branches.find((branch) => branch.id === createdSale?.branchId)?.name ??
    createdSale?.branchId ??
    '';

  return (
    <>
      <PageHeader
        title="ขายส่งและออกบิล"
        subtitle="สร้างรายการขายเงินสด ตัดสต๊อก และพิมพ์เอกสาร A4 ในขั้นตอนเดียว"
      />

      {isPreparing ? (
        <div
          aria-label="กำลังเตรียมข้อมูลขายส่ง"
          className="grid animate-pulse items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]"
        >
          <div className="space-y-5">
            <div className="bo-card h-28 bg-bg-warm" />
            <div className="bo-card h-72 bg-bg-warm" />
          </div>
          <div className="bo-card h-64 bg-bg-warm" />
        </div>
      ) : hasLoadError ? (
        <div
          role="alert"
          className="bo-card flex flex-col items-start gap-4 p-5 sm:p-6"
        >
          <div>
            <h2 className="text-h3 font-bold text-ink">
              เตรียมข้อมูลสำหรับขายส่งไม่สำเร็จ
            </h2>
            <p className="mt-1 text-body text-ink-2">
              กรุณาตรวจสอบการเชื่อมต่อแล้วลองโหลดข้อมูลอีกครั้ง
            </p>
          </div>
          <Btn
            type="button"
            onClick={() => {
              void branchesQuery.refetch();
              void productsQuery.refetch();
              void branchPricingQuery.refetch();
            }}
          >
            ลองอีกครั้ง
          </Btn>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]"
        >
          <div className="space-y-5">
            <Card
              title={
                <span className="flex items-baseline gap-2">
                  <span>รายการสินค้า</span>
                  <span className="text-sm-label font-semibold text-ink-3">
                    {selectedLineCount} รายการ
                  </span>
                </span>
              }
            >
              {activeProducts.length === 0 && (
                <div className="mb-4 rounded-btn border border-warn/30 bg-warn-light px-3 py-2 text-sm-label text-warn">
                  ยังไม่มีสินค้าที่เปิดขาย กรุณาเปิดใช้งานสินค้าก่อนออกบิล
                </div>
              )}

              <div
                className="mb-5"
                ref={searchRef}
                onKeyDownCapture={(event) => {
                  if (
                    event.key !== 'Enter' ||
                    !(event.target instanceof HTMLInputElement)
                  )
                    return;
                  event.preventDefault();
                  event.stopPropagation();
                  const query = event.target.value.trim();
                  if (!query) return;
                  const normalized = query.toLocaleLowerCase();
                  const match =
                    activeProducts.find(
                      (product) =>
                        product.name.toLocaleLowerCase() === normalized ||
                        product.id.toLocaleLowerCase() === normalized ||
                        product.units.some((unit) => unit.barcode === query),
                    ) ??
                    activeProducts.find((product) =>
                      product.name.toLocaleLowerCase().includes(normalized),
                    );
                  if (match) addProduct(match.id, query);
                  else setSearchError(`ไม่พบสินค้า ${query}`);
                }}
                onInput={() => setSearchError('')}
              >
                <Field label="ค้นหาเพื่อเพิ่มสินค้า">
                  <ProductSelect
                    key={searchKey}
                    products={activeProducts}
                    value=""
                    onChange={addProduct}
                    placeholder="ค้นหาสินค้าแล้วกด Enter หรือสแกนบาร์โค้ด"
                  />
                </Field>
                {searchError && (
                  <p role="alert" className="mt-2 text-sm-label text-danger">
                    {searchError}
                  </p>
                )}
              </div>
              <div className="hidden grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,0.65fr)_minmax(0,1fr)_minmax(0,0.9fr)_44px] gap-2 border-b border-line px-3 pb-2 text-sm-label font-bold text-ink-3 lg:grid">
                <div>สินค้า</div>
                <div>หน่วย</div>
                <div>จำนวน</div>
                <div>ราคา/หน่วย</div>
                <div className="text-right">ยอดรวม</div>
                <span className="sr-only">จัดการ</span>
              </div>

              <div className="divide-y divide-line-2">
                {lines.map((line, index) => {
                  const product = productById.get(line.productId);
                  const normalized = normalizedLines[index];
                  return (
                    <div
                      key={index}
                      className="relative grid gap-3 py-4 first:pt-2 lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,0.65fr)_minmax(0,1fr)_minmax(0,0.9fr)_44px] lg:items-end lg:px-3 lg:gap-2 [&>*]:min-w-0"
                    >
                      <div>
                        <div className="mb-1.5 flex items-center gap-2 text-sm-label font-semibold text-ink-2 lg:hidden">
                          <span className="flex h-5 min-w-5 items-center justify-center rounded-chip bg-bg-warm px-1.5 text-sm-label font-bold text-ink-3">
                            {index + 1}
                          </span>
                          สินค้า <span className="text-primary">*</span>
                        </div>
                        <ProductSelect
                          products={activeProducts}
                          value={line.productId}
                          onChange={(value, barcode) =>
                            selectProduct(index, value, barcode)
                          }
                          placeholder="ค้นหาชื่อหรือยิงบาร์โค้ด..."
                          disabled={activeProducts.length === 0}
                        />
                      </div>

                      <Field
                        label={
                          <span className="lg:hidden">
                            หน่วย <span className="text-primary">*</span>
                          </span>
                        }
                      >
                        <Select
                          aria-label={`หน่วยแถว ${index + 1}`}
                          value={line.unitId}
                          onChange={(event) =>
                            selectUnit(index, event.target.value)
                          }
                          disabled={!product}
                        >
                          <option value="">เลือกหน่วย</option>
                          {product?.units.map((unit, unitIndex) => (
                            <option
                              key={unitId(product, unit, unitIndex)}
                              value={unitId(product, unit, unitIndex)}
                            >
                              {unit.unit}
                            </option>
                          ))}
                        </Select>
                      </Field>

                      <Field
                        label={
                          <span className="lg:hidden">
                            จำนวน <span className="text-primary">*</span>
                          </span>
                        }
                      >
                        <Input
                          aria-label={`จำนวนแถว ${index + 1}`}
                          inputMode="decimal"
                          value={line.quantity}
                          onChange={(event) =>
                            setLine(index, { quantity: event.target.value })
                          }
                          className="w-full min-w-0 tabular-nums"
                        />
                      </Field>

                      <Field
                        label={
                          <span className="lg:hidden">
                            ราคา/หน่วย (บาท){' '}
                            <span className="text-primary">*</span>
                          </span>
                        }
                      >
                        <Input
                          aria-label={`ราคาต่อหน่วยแถว ${index + 1}`}
                          inputMode="decimal"
                          value={line.unitPriceBaht}
                          onChange={(event) =>
                            setLine(index, {
                              unitPriceBaht: event.target.value,
                            })
                          }
                          className="w-full min-w-0 tabular-nums"
                        />
                      </Field>

                      <div className="flex min-h-10 items-center justify-between border-t border-line-2 pr-12 pt-3 lg:block lg:border-0 lg:pr-0 lg:pt-0 lg:text-right">
                        <span className="text-sm-label font-semibold text-ink-3 lg:hidden">
                          ยอดรวม
                        </span>
                        <span className="font-bold tabular-nums text-ink">
                          {fmtAmount(normalized?.amount ?? 0)}
                          <span className="ml-1 text-sm-label font-medium text-ink-3">
                            บาท
                          </span>
                        </span>
                      </div>

                      <button
                        type="button"
                        disabled={lines.length === 1}
                        aria-label={`ลบสินค้าแถว ${index + 1}`}
                        title={`ลบสินค้าแถว ${index + 1}`}
                        onClick={() =>
                          setLines((current) =>
                            current.filter(
                              (_, lineIndex) => lineIndex !== index,
                            ),
                          )
                        }
                        className="absolute bottom-4 right-0 inline-flex h-11 w-11 items-center justify-center rounded-btn text-ink-3 transition-colors hover:bg-danger-light hover:text-danger focus:outline-none focus:ring-2 focus:ring-danger/20 disabled:pointer-events-none disabled:opacity-30 lg:static"
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          className="h-[18px] w-[18px]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-1 flex border-t border-line-2 pt-4">
                <Btn
                  type="button"
                  size="sm"
                  disabled={activeProducts.length === 0 || lines.length >= 100}
                  onClick={() =>
                    setLines((current) => [...current, blankLine()])
                  }
                  icon={
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                    </svg>
                  }
                >
                  เพิ่มรายการสินค้า
                </Btn>
              </div>
            </Card>

            <Card
              title="ข้อมูลบนเอกสาร"
              actions={
                <Btn
                  type="button"
                  size="sm"
                  aria-expanded={showCustomerFields}
                  aria-controls="wholesale-customer-fields"
                  onClick={() => setShowCustomerFields((current) => !current)}
                >
                  {showCustomerFields ? 'ซ่อนข้อมูลลูกค้า' : 'ระบุข้อมูลลูกค้า'}
                </Btn>
              }
            >
              <p className="mb-4 text-body text-ink-2">
                หากไม่ระบุ ระบบจะออกเอกสารในชื่อลูกค้าทั่วไป
              </p>

              {showCustomerFields && (
                <div
                  id="wholesale-customer-fields"
                  className="mb-4 grid gap-4 border-b border-line-2 pb-4 sm:grid-cols-2"
                >
                  <Field label="ชื่อบุคคล / บริษัท">
                    <Input
                      aria-label="ชื่อลูกค้า"
                      value={customer.name}
                      onChange={(event) =>
                        setCustomer((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                      maxLength={200}
                    />
                  </Field>
                  <Field label="เลขประจำตัวผู้เสียภาษี">
                    <Input
                      aria-label="เลขผู้เสียภาษีลูกค้า"
                      value={customer.taxId}
                      onChange={(event) =>
                        setCustomer((current) => ({
                          ...current,
                          taxId: event.target.value,
                        }))
                      }
                      maxLength={32}
                    />
                  </Field>
                  <Field label="เบอร์โทร">
                    <Input
                      aria-label="เบอร์โทรลูกค้า"
                      value={customer.phone}
                      onChange={(event) =>
                        setCustomer((current) => ({
                          ...current,
                          phone: event.target.value,
                        }))
                      }
                      maxLength={32}
                    />
                  </Field>
                  <Field label="ที่อยู่">
                    <Input
                      aria-label="ที่อยู่ลูกค้า"
                      value={customer.address}
                      onChange={(event) =>
                        setCustomer((current) => ({
                          ...current,
                          address: event.target.value,
                        }))
                      }
                      maxLength={1_000}
                    />
                  </Field>
                </div>
              )}

              <Field label="หมายเหตุบนเอกสาร">
                <textarea
                  aria-label="หมายเหตุบนเอกสาร"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={2_000}
                  placeholder="เช่น จุดส่งของ หรือชื่อผู้รับสินค้า"
                  className="min-h-20 resize-y rounded-btn border border-line bg-surface px-3 py-2 text-body text-ink placeholder:text-ink-4 hover:border-ink-3/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </Field>
            </Card>
          </div>

          <Card title="สรุปบิล" className="xl:sticky xl:top-5">
            <div className="mb-4 flex items-center gap-2 rounded-btn bg-ok-light px-3 py-2 text-sm-label font-bold text-ok">
              <span aria-hidden="true" className="h-2 w-2 rounded-full bg-ok" />
              ชำระเงินสดเมื่อส่งมอบ
            </div>

            <dl className="space-y-3 text-body">
              <div className="flex justify-between gap-4">
                <dt className="text-ink-3">สาขาที่ขาย</dt>
                <dd className="truncate text-right font-semibold text-ink">
                  {selectedBranchName || 'ทุกสาขา'}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-3">ลูกค้า</dt>
                <dd className="truncate text-right font-semibold text-ink">
                  {customerSummary}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-3">สินค้า</dt>
                <dd className="font-semibold text-ink">
                  {selectedLineCount} รายการ
                </dd>
              </div>
              <div className="border-t-2 border-ink pt-4">
                <dt className="text-sm-label font-bold text-ink-3">
                  ยอดรับเงิน
                </dt>
                <dd className="mt-1 flex items-baseline justify-end gap-1 text-[28px] font-extrabold leading-none tabular-nums text-primary">
                  {fmtAmount(totalAmount)}
                  <span className="text-sm-label font-semibold text-ink-3">
                    บาท
                  </span>
                </dd>
              </div>
            </dl>

            {formError && (
              <div
                role="alert"
                className="mt-4 rounded-btn border border-danger/30 bg-danger/5 p-3 text-sm-label text-danger"
              >
                {formError}
              </div>
            )}

            {!branchId && (
              <p role="status" className="mt-4 text-sm-label text-warn">
                เลือกสาขาที่ขายจากเมนูด้านข้างก่อนออกบิล
                ไม่สามารถออกบิลในมุมมองทุกสาขาได้
              </p>
            )}
            <Btn
              kind="primary"
              type="submit"
              className="mt-5 w-full whitespace-nowrap"
              disabled={
                !branchId ||
                createSale.isPending ||
                allowedBranches.length === 0 ||
                activeProducts.length === 0
              }
            >
              {createSale.isPending ? 'กำลังบันทึก...' : 'รับเงินสดและออกบิล'}
            </Btn>
            <p className="mt-3 text-center text-sm-label leading-relaxed text-ink-3">
              ยืนยันแล้วระบบจะบันทึกยอดขายและตัดสต๊อกทันที
            </p>
          </Card>
        </form>
      )}

      <WholesaleSalePrintModal
        open={!!createdSale}
        sale={createdSale}
        branchName={createdBranchName}
        settings={settings}
        onClose={() => setCreatedSale(null)}
      />
    </>
  );
}
